import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';
import { cn } from '@utils/index';
import {
  FAQ_IMPORTANT_CONTACTS,
  FAQ_IMPORTANT_TERMS,
  FAQ_INTRO,
  FAQ_ITEMS,
  type FaqItem,
  type FaqLink,
} from '../constants/faqs';

const linkClassName = 'text-primary font-semibold underline';

const renderFaqAnswer = (item: FaqItem) => {
  if (!item.links?.length) {
    return item.answer;
  }

  let answer = item.answer;
  const parts: React.ReactNode[] = [];

  item.links.forEach((link: FaqLink, index) => {
    const linkIndex = answer.indexOf(link.label);
    if (linkIndex === -1) {
      return;
    }

    if (linkIndex > 0) {
      parts.push(answer.slice(0, linkIndex));
    }

    parts.push(
      <a
        key={`${item.id}-link-${index}`}
        href={link.href}
        className={linkClassName}
        {...(link.external
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {})}
      >
        {link.label}
      </a>
    );

    answer = answer.slice(linkIndex + link.label.length);
  });

  if (answer) {
    parts.push(answer);
  }

  return parts.length ? parts : item.answer;
};

export const FAQsPage = () => {
  return (
    <div className="flex flex-col gap-10 max-xs:px-1">
      <h2 className="text-4xl 2xl:text-5xl font-extrabold text-gray-800 tracking-wide">
        Some frequently asked questions
      </h2>

      {/* <div className="border-t border-b border-[#E5E2DC] py-8 max-xl:w-full w-[80%]">
        <h3 className="text-2xl max-md:text-xl font-semibold text-primary mb-5">
          {FAQ_INTRO.title}
        </h3>
        <div className="flex flex-col gap-5 text-xl max-md:text-lg text-gray-500 leading-relaxed">
          {FAQ_INTRO.paragraphs.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
      </div> */}

      <Accordion type="single" collapsible className="w-full">
        {FAQ_ITEMS.map((item) => (
          <AccordionItem key={item.id} value={item.id} className="border-[#E5E2DC]">
            <AccordionTrigger
              iconProps={{ className: 'hidden' }}
              className={cn(
                'w-full justify-start text-2xl max-md:text-xl font-semibold text-gray-600 hover:no-underline py-6',
                'data-[state=open]:text-primary [&>svg]:hidden'
              )}
            >
              {item.question}
            </AccordionTrigger>
            <AccordionContent className="text-xl max-md:text-lg">
              <div className="text-xl max-md:text-lg text-gray-500 leading-relaxed pb-6">
                {renderFaqAnswer(item)}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <section className="flex flex-col gap-5">
        <h3 className="text-3xl max-md:text-2xl font-bold text-gray-800">
          Important things to know:
        </h3>
        <ul className="flex flex-col gap-4 text-xl max-md:text-lg text-gray-600">
          {FAQ_IMPORTANT_TERMS.map((item) => (
            <li key={item.term}>
              <span className="font-bold text-gray-800">{item.term}</span>
              {' - '}
              {item.description}
            </li>
          ))}
        </ul>
      </section>

      <section className="flex flex-col gap-5 pb-4">
        <h3 className="text-3xl max-md:text-2xl font-bold text-gray-800">
          Important phone numbers:
        </h3>
        <ul className="flex flex-col gap-5 text-xl max-md:text-lg text-gray-600">
          {FAQ_IMPORTANT_CONTACTS.map((item, index) => (
            <li key={index}>
              {item.label}{' '}
              {item.linkHref ? (
                <a
                  href={item.linkHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold underline"
                >
                  {item.linkLabel}
                </a>
              ) : (
                <>
                  <a
                    href={item.href}
                    className="text-primary font-semibold underline"
                  >
                    {item.value}
                  </a>
                  {'suffix' in item && item.suffix ? (
                    <> {item.suffix}</>
                  ) : null}
                  {item.extra && item.extraHref && (
                    <>
                      {' '}
                      or email{' '}
                      <a
                        href={item.extraHref}
                        className="text-primary font-semibold underline"
                      >
                        {item.extra}
                      </a>
                    </>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};
